import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { useApp } from "../state/AppContext";
import { useThreadDetail } from "../state/hooks";
import { TemplatesButton } from "./TemplatesButton";
import { AttachmentPreview, isPreviewable } from "./AttachmentPreview";
import { LabelPicker } from "./LabelPicker";
import type { ThreadDetail, MessageAttachment } from "../../shared/types";

const TIER_LABEL: Record<string, string> = { now: "Needs you now", vendor: "Waiting on vendor", fyi: "FYI" };
const TIER_COLOR: Record<string, string> = { now: "var(--lb-red-2)", vendor: "var(--lb-amber-2)", fyi: "var(--lb-blue-2)" };

// Iframe content is a separate document — our theme's CSS variables don't
// cross that boundary, so the dark/light defaults are hardcoded to match
// tokens.css. Low-specificity `html, body` rules only: anything the email
// itself declares (its own colors, a table bgcolor, etc.) still wins via
// normal cascade, so a styled/branded email renders as designed — this only
// fills in a sensible default for plain emails that don't set their own.
function wrapEmailHtml(html: string, theme: "graphite" | "paper"): string {
  // A true dark background broke plain emails that assume light text sits
  // on a light canvas. Graphite gets a soft light grey instead of pure
  // white (still light enough for normal dark email text, easier on the
  // eyes against the app's dark chrome); Paper — already light — uses its
  // own theme palette so it blends in naturally. Either way, only a default:
  // an email that explicitly styles itself still wins via normal cascade.
  const bg = theme === "paper" ? "#f4f2ed" : "#e2e2e0";
  const fg = theme === "paper" ? "#262420" : "#1c1c1a";
  const link = theme === "paper" ? "#35618f" : "#1a56a8";

  // Many order-confirmation/marketing emails are built as fixed-width
  // tables (assuming a ~600-700px desktop client) that just overflow a
  // narrower reading pane instead of reflowing. `!important` here is
  // deliberate — this isn't a default like the color rules above, it's
  // forcing genuinely fixed-width layout tables/images down to fit, which
  // an email's own narrower CSS wouldn't otherwise be overridden by.
  const responsive = `table,td{max-width:100%!important;}img{max-width:100%!important;height:auto!important;}body{word-wrap:break-word;overflow-wrap:break-word;}`;
  const style = `<style>html,body{background:${bg};color:${fg};font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif;margin:0;padding:8px 4px;}a{color:${link};}${responsive}</style>`;

  const headMatch = html.match(/<head[^>]*>/i);
  if (headMatch) return html.replace(headMatch[0], `${headMatch[0]}${style}`);
  const htmlMatch = html.match(/<html[^>]*>/i);
  if (htmlMatch) return html.replace(htmlMatch[0], `${htmlMatch[0]}<head>${style}</head>`);
  return `<!doctype html><html><head>${style}</head><body>${html}</body></html>`;
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString();
}

export function ReadingPane({
  mailboxId,
  threadId,
  onAfterAction,
  replyTrigger,
  labelTrigger,
}: {
  mailboxId: string;
  threadId: string | null;
  onAfterAction: () => void;
  replyTrigger?: number;
  labelTrigger?: number;
}) {
  const { me } = useApp();
  const theme = me?.prefs.theme ?? "graphite";
  const { thread, error, reload } = useThreadDetail(mailboxId, threadId);
  const [replying, setReplying] = useState(false);
  const [draft, setDraft] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [undo, setUndo] = useState<{ pendingId: string; sendAfter: number } | null>(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [asksVendor, setAsksVendor] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<MessageAttachment | null>(null);
  const [expandThread, setExpandThread] = useState(false);
  const [labelPickerOpen, setLabelPickerOpen] = useState(false);

  useEffect(() => {
    setExpandThread(false);
  }, [threadId]);

  // Opening a thread can change how it looks in the list (marked read here,
  // triaged for the first time, etc.) — the list has its own separate,
  // already-fetched copy of that data, so it won't reflect this on its own.
  // Refresh it once per thread actually opened (not on every render).
  const refreshedListFor = useRef(new Set<string>());
  useEffect(() => {
    if (!thread || refreshedListFor.current.has(thread.id)) return;
    refreshedListFor.current.add(thread.id);
    onAfterAction();
  }, [thread, onAfterAction]);

  function attachmentUrl(a: MessageAttachment): string {
    return `/api/mailboxes/${mailboxId}/threads/${threadId}/attachments/${a.gmailMessageId}/${a.attachmentId}?filename=${encodeURIComponent(a.filename)}&mimeType=${encodeURIComponent(a.mimeType)}`;
  }

  useEffect(() => {
    if (!undo) return;
    const interval = setInterval(() => setNowTick(Date.now()), 250);
    return () => clearInterval(interval);
  }, [undo]);

  useEffect(() => {
    if (replyTrigger) setReplying(true);
  }, [replyTrigger]);

  useEffect(() => {
    if (labelTrigger) setLabelPickerOpen(true);
  }, [labelTrigger]);

  useEffect(() => {
    setReplying(false);
    setDraft("");
  }, [threadId]);

  if (!threadId) {
    return <div className="gm-reading" style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "var(--gm-text-4)" }}>Select a thread</div>;
  }
  if (error) {
    return (
      <div className="gm-reading" style={{ padding: 22.4 }}>
        <div className="card" style={{ color: "var(--lb-red-2)" }}>Couldn't load this thread: {error}</div>
      </div>
    );
  }
  if (!thread) return <div className="gm-reading" />;

  async function doAction(action: string, extra: Record<string, unknown> = {}) {
    await api.post(`/api/mailboxes/${mailboxId}/threads/${threadId}/action`, { action, ...extra });
    onAfterAction();
    reload();
  }

  async function sendReply() {
    if (!draft.trim()) return;
    const res = await api.post<{ sent: boolean; pendingId?: string; sendAfter?: number }>(
      `/api/mailboxes/${mailboxId}/threads/${threadId}/reply`,
      { body: draft, asksVendor },
    );
    setDraft("");
    setReplying(false);
    setAsksVendor(false);
    if (!res.sent && res.pendingId && res.sendAfter) {
      const msLeft = res.sendAfter - Date.now();
      if (msLeft > 0) {
        setUndo({ pendingId: res.pendingId, sendAfter: res.sendAfter });
        const pendingId = res.pendingId;
        setTimeout(() => {
          api.post(`/api/mailboxes/${mailboxId}/pending-sends/${pendingId}/commit`).catch(() => {});
          setUndo((u) => (u?.pendingId === pendingId ? null : u));
          reload();
        }, msLeft);
      }
    } else {
      reload();
    }
  }

  async function cancelUndo() {
    if (!undo) return;
    await api.post(`/api/mailboxes/${mailboxId}/pending-sends/${undo.pendingId}/cancel`);
    setUndo(null);
    setReplying(true);
  }

  async function addNote() {
    if (!noteText.trim()) return;
    await api.post(`/api/mailboxes/${mailboxId}/threads/${threadId}/notes`, { text: noteText });
    setNoteText("");
    reload();
  }

  const t: ThreadDetail = thread;
  const showDirections = replying && !draft.trim() && t.directions.length > 0;

  return (
    <div className="gm-reading">
      <div className="gm-reading-scroll">
        <div className="gm-reading-header">
          <h1 className="gm-reading-subject">{t.subject || "(no subject)"}</h1>
          <div style={{ fontSize: 12, color: "var(--gm-text-3)" }}>{t.from}</div>
          <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center" }}>
            {(["now", "vendor", "fyi"] as const).map((tierOption) => (
              <button
                key={tierOption}
                className="chip"
                style={{
                  cursor: "pointer",
                  padding: "4px 9px",
                  borderRadius: 5,
                  border: t.tier === tierOption ? `1px solid ${TIER_COLOR[tierOption]}` : "1px solid var(--gm-line)",
                  background: t.tier === tierOption ? TIER_COLOR[tierOption] : "var(--gm-surface-2)",
                  color: t.tier === tierOption ? "var(--gm-bg-deep)" : "var(--gm-text-3)",
                }}
                onClick={() => doAction("set_tier", { tier: tierOption })}
              >
                {TIER_LABEL[tierOption]}
              </button>
            ))}
            {t.category && <span className="chip chip-neutral">{t.category}</span>}
          </div>
          <div className="gm-reading-actions">
            <button className="btn" onClick={() => setReplying(true)}>Reply <span className="hint">R</span></button>
            <button className="btn" onClick={() => doAction(t.archived ? "unarchive" : "archive")}>
              {t.archived ? "Move to inbox" : "Archive"} <span className="hint">E</span>
            </button>
            <button className="btn btn-danger" onClick={() => doAction("trash")}>Delete</button>
            {!t.spam ? (
              <button className="btn" onClick={() => doAction("spam")}>Mark spam</button>
            ) : (
              <button className="btn" onClick={() => doAction("not_spam")}>Not spam</button>
            )}
            <button className="btn" onClick={() => setLabelPickerOpen(true)}>Label <span className="hint">L</span></button>
          </div>
        </div>

        {labelPickerOpen && (
          <LabelPicker
            mailboxId={mailboxId}
            threadId={t.id}
            appliedLabelIds={t.labelIds}
            onChange={() => {
              onAfterAction();
              reload();
            }}
            onClose={() => setLabelPickerOpen(false)}
          />
        )}

        <div style={{ margin: "11.2px 0" }}>
          <button className="btn btn-ghost" onClick={() => setNotesOpen((v) => !v)}>
            {notesOpen ? "Hide notes" : `Internal notes (${t.notes.length})`}
          </button>
          {notesOpen && (
            <div className="card" style={{ marginTop: 8 }}>
              {t.notes.map((n) => (
                <div key={n.id} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11.5, color: "var(--gm-text-3)" }}>
                    {n.who.name} · {relativeTime(n.timeIso)}
                  </div>
                  <div style={{ fontSize: 12.5 }}>{n.text}</div>
                </div>
              ))}
              <div style={{ display: "flex", gap: 6 }}>
                <input className="input" placeholder="Add a note…" value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} />
                <button className="btn" onClick={addNote}>Add</button>
              </div>
            </div>
          )}
        </div>

        {t.summary && (
          <div className="gm-summary-card">
            <div className="section-header" style={{ marginBottom: 6 }}>Summary</div>
            <div style={{ fontSize: 13 }}>{t.summary}</div>
            {t.tasks.length > 0 && (
              <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none" }}>
                {t.tasks.map((task, i) => (
                  <li key={i} style={{ fontSize: 12.5 }}>
                    <span style={{ color: "var(--gm-accent-deep)" }}>→ </span>
                    {task}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {t.messages.length > 1 && (
          <button className="btn btn-ghost" style={{ marginBottom: 8 }} onClick={() => setExpandThread((v) => !v)}>
            {expandThread
              ? `Collapse ${t.messages.length - 1} earlier message${t.messages.length - 1 > 1 ? "s" : ""}`
              : `Show ${t.messages.length - 1} earlier message${t.messages.length - 1 > 1 ? "s" : ""}`}
          </button>
        )}

        {(expandThread ? t.messages : t.messages.slice(-1)).map((m) => {
          const i = t.messages.indexOf(m);
          return (
          <div key={m.gmailMessageId} className={`gm-message-card${i < t.messages.length - 1 ? " is-older" : ""}`}>
            <div className="gm-message-head">
              <div className="avatar avatar-round" style={{ width: 26, height: 26 }}>{m.who.slice(0, 1).toUpperCase()}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>{m.who}</div>
                {m.to && (
                  <div style={{ fontSize: 11, color: "var(--gm-text-4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    to {m.to}
                  </div>
                )}
              </div>
              <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--gm-text-4)", flexShrink: 0 }}>{relativeTime(m.timeIso)}</div>
            </div>
            {m.bodyHtml ? (
              // Sandboxed with no allow-scripts / allow-same-origin: this
              // renders the sender's actual HTML (fonts, colors, layout)
              // faithfully, while making it impossible for a malicious
              // email to run script or reach our session — the iframe gets
              // an opaque, cookie-less origin no matter what's inside it.
              <iframe
                title="Message body"
                srcDoc={wrapEmailHtml(m.bodyHtml, theme)}
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                style={{ width: "100%", height: 420, border: "none", borderRadius: 6 }}
              />
            ) : (
              <div className="gm-message-body">{m.bodyText}</div>
            )}
            {m.attachments.length > 0 && (
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                {m.attachments.map((a) =>
                  isPreviewable(a.mimeType) ? (
                    <button
                      key={a.attachmentId}
                      className="chip chip-neutral"
                      style={{ cursor: "pointer", border: "none" }}
                      onClick={() => setPreviewAttachment(a)}
                    >
                      {a.filename}
                    </button>
                  ) : (
                    <a
                      key={a.attachmentId}
                      className="chip chip-neutral"
                      href={attachmentUrl(a)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ cursor: "pointer" }}
                    >
                      {a.filename}
                    </a>
                  ),
                )}
              </div>
            )}
          </div>
          );
        })}

        {showDirections && (
          <div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div className="section-header">Reply directions</div>
              <div style={{ marginLeft: "auto" }}>
                <TemplatesButton mailboxId={mailboxId} onPick={setDraft} />
              </div>
            </div>
            <div className="gm-directions-grid">
              {t.directions.map((d, i) => (
                <button
                  key={i}
                  className="gm-direction-card"
                  onClick={() => {
                    setDraft(d.body);
                    setAsksVendor(d.asksVendor);
                  }}
                >
                  {d.skill && <div className="chip chip-neutral" style={{ marginBottom: 6 }}>Skill · {d.skill}</div>}
                  <div style={{ fontSize: 12.5, fontWeight: 500 }}>{d.label}</div>
                  <div style={{ fontSize: 11.5, color: "var(--gm-text-3)" }}>{d.blurb}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {replying && (
        <div className="gm-draft-editor">
          <textarea
            className="input"
            style={{ minHeight: 160, resize: "vertical" }}
            placeholder="Write your reply, or pick a direction above"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary" onClick={sendReply}>Send <span className="hint">⌘⏎</span></button>
            <button className="btn btn-ghost" onClick={() => { setReplying(false); setDraft(""); }}>Cancel</button>
          </div>
        </div>
      )}

      {undo && (
        <div className="gm-undo-bar">
          <span>Sending in {Math.max(0, Math.ceil((undo.sendAfter - nowTick) / 1000))}s…</span>
          <button className="btn btn-ghost" onClick={cancelUndo}>Undo</button>
        </div>
      )}

      {previewAttachment && (
        <AttachmentPreview
          filename={previewAttachment.filename}
          mimeType={previewAttachment.mimeType}
          url={attachmentUrl(previewAttachment)}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  );
}
